import { forwardRef, useImperativeHandle } from "react";
import Spinner from "react-bootstrap/Spinner";
import { GridRowClickEvent } from "@progress/kendo-react-grid";
import KendoTableClientState from "@/components/shared/tables/KendoTableClientState";
import { User, UserOrg } from "@/models/user.tsx";

type UserWizardSearchResultsProps = {
  searchResults: User<UserOrg>[];
  usersLoading?: boolean;
  onSelectUser: (user: User<UserOrg>) => void;
  onCreateNew: () => void;
  onBack: () => void;
};

export type UserSearchResultsRef = {
  handleBack: () => void;
};

const columns = [
  { field: "userName", title: "Username", width: "200px" },
  { field: "firstName", title: "First Name", width: "150px" },
  { field: "lastName", title: "Last Name", width: "150px" },
  { field: "email", title: "Email", minWidth: 220 },
];

export const UserWizardSearchResults = forwardRef<
  UserSearchResultsRef,
  UserWizardSearchResultsProps
>(function UserWizardSearchResults(
  { searchResults, usersLoading, onSelectUser, onCreateNew, onBack },
  ref
) {
  const resultsCount = searchResults.length;

  const handleRowClick = (event: GridRowClickEvent) => {
    const user = event.dataItem.__raw as User<UserOrg> | undefined;
    if (user) {
      onSelectUser(user);
    }
  };

  useImperativeHandle(
    ref,
    () => ({
      handleBack: onBack,
    }),
    [onBack]
  );

  const tableData = searchResults.map((u) => ({
    userName: u.userName || "",
    firstName: u.firstName || "",
    lastName: u.lastName || "",
    email: u.email || "",
    __raw: u,
  }));

  return (
    <div style={{ padding: "0 24px 24px", background: "white" }}>
      {/* Result count header */}
      {!usersLoading && (
        <div style={{ textAlign: "center", marginBottom: "24px" }}>
          <h6
            style={{
              fontWeight: "600",
              marginBottom: "12px",
              fontSize: "16px",
              color: "#333",
            }}
          >
            {resultsCount} Potential Match{resultsCount !== 1 ? "es" : ""} Found
          </h6>
          <p style={{ margin: 0, fontSize: "14px", color: "#666" }}>
            Select a record to pre-fill the user profile form.
          </p>
        </div>
      )}

      {usersLoading ? (
        <div
          style={{
            display: "flex",
            flexDirection: "column",
            alignItems: "center",
            justifyContent: "center",
            padding: "48px 0",
            gap: "12px",
          }}
        >
          <Spinner
            animation="border"
            variant="primary"
            style={{ width: "2rem", height: "2rem" }}
          />
          <span style={{ fontSize: "14px", color: "#666" }}>Searching...</span>
        </div>
      ) : (
        <div style={{ background: "white", borderRadius: "4px" }}>
          <div style={{ maxHeight: "360px", overflowY: "auto" }}>
            <KendoTableClientState
              data={tableData}
              columns={columns}
              onRowClick={handleRowClick}
              filterable={false}
              noItemsMessage="No matching users found."
              initialTake={searchResults.length || 10}
            />
          </div>
        </div>
      )}

      {!usersLoading && (
        <>
          <div
            style={{
              position: "relative",
              margin: "28px 0 0",
              display: "flex",
              justifyContent: "center",
            }}
          >
            <span
              style={{
                background: "white",
                padding: "0 20px",
                fontSize: "14px",
                fontWeight: "600",
                color: "#333",
              }}
            >
              {resultsCount > 0
                ? "Or, if none of the above match:"
                : "The user doesn't exist yet:"}
            </span>
          </div>

          <div style={{ textAlign: "center", marginTop: "24px" }}>
            <button
              type="button"
              onClick={onCreateNew}
              style={{
                padding: "12px 40px",
                background: "#5b9bd5",
                border: "none",
                borderRadius: "4px",
                color: "white",
                fontSize: "15px",
                cursor: "pointer",
                fontWeight: "600",
                boxShadow: "0 2px 4px rgba(0,0,0,0.1)",
              }}
            >
              Continue with New Record
            </button>
          </div>
        </>
      )}
    </div>
  );
});
